import { Injector } from '@angular/core';
import { FormID } from '@finxone-platform/form-action';
import type { CustomerMatchRequest } from '@finxone-platform/shared/app-sdk';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  ButtonActionType,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { firstValueFrom } from 'rxjs';
import { ZonePageUrlProvider } from '../../../../Modules/zones/zone-url-provider';
import { AccountService } from '../../../services/account-service/account-service.service';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { OnboardingDetailsService } from '../../../services/onboarding-details/onboarding-details.service';
import { handleRedirection } from '../cta-redirection.utils';

const pending = new WeakSet<OnboardingDetailsService>();
const action = ButtonActionType.CHECK_CUSTOMER_AND_CONTINUE_ONBOARDING;

export function customerMatchRequest(values: Record<string, any>): CustomerMatchRequest | undefined {
  const dob = values['dob'];
  let date = '';
  if (dob instanceof Date && Number.isFinite(dob.getTime())) {
    // Birthday widgets normalize to UTC midnight; other form inputs may retain local midnight.
    date =
      dob.getUTCHours() === 0 && dob.getUTCMinutes() === 0 && dob.getUTCSeconds() === 0
        ? dob.toISOString().slice(0, 10)
        : `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`;
  } else if (typeof dob === 'string') {
    date = dob.slice(0, 10);
  }
  const text = (key: string) => (typeof values[key] === 'string' ? values[key].trim() : '');
  const phone = text('phoneNumber');
  if (!phone) return;
  const request: CustomerMatchRequest = {
    firstName: text('firstName'),
    lastName: text('lastName'),
    email: text('email'),
    phoneNumber: phone.startsWith('+') ? phone : `${text('countryCode')}${phone}`,
    dateOfBirth: date,
  };
  return Object.values(request).every(Boolean) && /^\d{4}-\d{2}-\d{2}$/.test(date) ? request : undefined;
}

function customerMatchingWaitMessage(error: unknown): string {
  const response = error as {
    error?: { retryAfterSeconds?: unknown };
    headers?: { get(name: string): string | null };
  };
  const retryAfter = Number(response.error?.retryAfterSeconds ?? response.headers?.get('Retry-After'));
  if (!Number.isFinite(retryAfter) || retryAfter <= 0 || retryAfter > 86400) {
    return 'Too many attempts. Please wait before trying again.';
  }
  const minutes = Math.ceil(retryAfter / 60);
  return `Too many attempts. Please try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
}

export async function checkCustomerAndContinueOnboarding(
  injector: Injector,
  widgetAttributes: UiZoneWidgetAttributeConfig,
): Promise<void> {
  const detailsService = injector.get(OnboardingDetailsService);
  if (pending.has(detailsService)) return;
  const zone = injector.get(ZonePageUrlProvider);
  const alerts = injector.get(AlertHandlerService);
  const values = detailsService.validateAndSaveDetails(
    widgetAttributes.widgetProperties?.['parentForm'] || FormID.ONBOARDING_FLOW,
    zone,
  );
  if (!values) return;
  const request = customerMatchRequest(values);
  if (!request) {
    alerts.showAlertFn('error', 'Please complete your name, email, phone number and date of birth.');
    return;
  }
  const signals = injector.get(CtaButtonSignalService);
  pending.add(detailsService);
  signals.setSignal({ [action]: false });
  signals.setButtonLoadingSignal({ [action]: true });
  try {
    const response = await firstValueFrom(injector.get(AccountService).checkCustomerMatch(request));
    const outcome =
      response.result === 'no-match'
        ? ActionButtonOutcomesType.SUCCESS
        : response.result === 'partial-match'
          ? ActionButtonOutcomesType.ALTERNATE_1
          : response.result === 'match'
            ? ActionButtonOutcomesType.ALTERNATE_2
            : undefined;
    const route = outcome && widgetAttributes.widgetProperties?.actionNavigationConfigurations?.[outcome];
    if (!route || !(route.pageId || route.selectedPageId || route.selectedPageUrl || route.externalUrl)) {
      alerts.showAlertFn('error', 'We could not continue. Please contact support.');
      return;
    }
    if (response.result !== 'no-match') {
      try {
        await injector.get(WorkflowFormsService).cancelFormProgress(FormID.ONBOARDING_FLOW);
      } catch {
        alerts.showAlertFn('error', 'We could not close your application. Please try again.');
        return;
      }
    }
    handleRedirection(widgetAttributes.widgetProperties, injector, outcome, true, true, action);
  } catch (error) {
    alerts.showAlertFn(
      'error',
      (error as { status?: number })?.status === 429
        ? customerMatchingWaitMessage(error)
        : 'We could not check your details. Please try again later.',
    );
  } finally {
    pending.delete(detailsService);
    signals.setSignal({ [action]: true });
    signals.setButtonLoadingSignal({ [action]: false });
  }
}
